import { useState, useEffect, useMemo } from "react";
import "../app-time-off/AppCompanyHolidays.css";
import "./AppAddress.css";
import { Address, US_STATES, isValidPostalCode, formatPostalCode } from "winslow-core";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Label } from "../../../ds2/components/Label";

type AppAddressProps = {
    value?: Address;
    onChange: (address: Address) => void;
    disabled?: boolean;
};

export const AppAddress = ({ value, onChange, disabled = false }: AppAddressProps) => {
    const [address, setAddress] = useState<Address>({
        line1: value?.line1 || "",
        line2: value?.line2 || "",
        postal_code: value?.postal_code || "",
        city: value?.city || "",
        state: value?.state || "CA",
        country: value?.country || "",
    });

    useEffect(() => {
        if (value) {
            setAddress({
                line1: value.line1 || "",
                line2: value.line2 || "",
                postal_code: value.postal_code || "",
                city: value.city || "",
                state: value.state || "CA",
                country: value.country || "US",
            });
        }
    }, [value]);

    const stateOptions: GenericSelectOption[] = useMemo(() => {
        return US_STATES.map((state: { code: string; name: string }) => ({
            displayName: `${state.code} - ${state.name}`,
            value: state.code,
        }));
    }, []);

    const selectedState: GenericSelectOption | undefined = useMemo(() => {
        if (!address.state) return undefined;
        const state = US_STATES.find((s: { code: string; name: string }) => s.code === address.state.toUpperCase());
        return state ? {
            displayName: `${state.code} - ${state.name}`,
            value: state.code,
        } : undefined;
    }, [address.state]);

    const handleFieldChange = (field: keyof Address, newValue: string) => {
        const updatedAddress = {
            ...address,
            [field]: newValue,
        };
        setAddress(updatedAddress);
        onChange(updatedAddress);
    };

    return (
        <div className="app-address">
            <div className="app-address-fields">
                <div className="app-address-column-left">
                    <Label required={true}>
                        Address Line 1
                    </Label>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            placeholder="Enter street address"
                            value={address.line1}
                            disabled={disabled}
                            onChange={(e) => handleFieldChange("line1", e.target.value)}
                        />
                    </div>

                    <Label>
                        Address Line 2
                    </Label>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            placeholder="Apartment, suite, etc. (optional)"
                            value={address.line2 || ""}
                            disabled={disabled}
                            onChange={(e) => handleFieldChange("line2", e.target.value)}
                        />
                    </div>

                    <Label required={true}>
                        City
                    </Label>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            placeholder="Enter city"
                            value={address.city}
                            disabled={disabled}
                            onChange={(e) => handleFieldChange("city", e.target.value)}
                        />
                    </div>
                </div>

                <div className="app-address-column-right">
                    <Label required={true}>
                        State
                    </Label>
                    <div className="app-custom-property-value">
                        <GenericSelect
                            options={stateOptions}
                            selected={selectedState}
                            disabled={disabled}
                            onChange={(value: string) => {
                                handleFieldChange("state", value);
                            }}
                            maxHeight="200px"
                        />
                    </div>

                    <Label 
                        required={true}
                        invalid={!!address.postal_code && address.postal_code.trim().length > 0 && !isValidPostalCode(address.postal_code)}
                        invalidReason="Please enter a valid postal code (##### or #####-####)"
                    >
                        Postal Code
                    </Label>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            placeholder="#####-####"
                            value={address.postal_code}
                            maxLength={10}
                            disabled={disabled}
                            onChange={(e) => {
                                const formatted = formatPostalCode(e.target.value);
                                handleFieldChange("postal_code", formatted);
                            }}
                        />
                    </div>

                    <Label>
                        Country
                    </Label>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            disabled={true}
                            placeholder="Enter country (optional)"
                            value={address.country || ""}
                            // disabled={disabled}
                            onChange={(e) => handleFieldChange("country", e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

